import { DayOfWeek } from '@caresend/types';
import { initTimeZoneDate, getSpecificTimeOnDate, formatLocalDate, isLocationOrigin, getTimeZoneFromLocation, getLatestSupplyArrivalTimestamp, checkProcedureCollectionDateForTimeSlot, checkProcedureCollectionTimeForTimeSlot } from '@caresend/utils';
import dayjs from 'dayjs';
import { getStore } from '../../../helpers/initStore.js';

const FRIDAY = 5;
const SATURDAY = 6;
const SUNDAY = 0;
const DAY_IN_HOURS = 24;
const getCanPerformProcedureCollections = (procedures, startTime, timeZone) => Object.values(procedures).every((procedure) => {
    const { collectionDate, collectionTime, collectOn, collectEnd, } = procedure !== null && procedure !== void 0 ? procedure : {};
    const dateCheck = checkProcedureCollectionDateForTimeSlot(startTime, timeZone, collectOn === null || collectOn === void 0 ? void 0 : collectOn.timestamp, collectEnd === null || collectEnd === void 0 ? void 0 : collectEnd.timestamp, collectionDate);
    if (!dateCheck)
        return false;
    const timeCheck = checkProcedureCollectionTimeForTimeSlot(startTime, timeZone, collectionTime);
    return timeCheck;
});
const getHasLessThan72HoursSampleStability = (labPlaceGroupID, supplyInstructionConfigOptions) => {
    if (!labPlaceGroupID)
        return false;
    return Object.values(supplyInstructionConfigOptions).some((configOptions) => {
        const placeGroupSupplyInstructions = configOptions === null || configOptions === void 0 ? void 0 : configOptions.supplyInstructionsByPlaceGroup[labPlaceGroupID];
        if (!placeGroupSupplyInstructions)
            return false;
        const mailInInstructions = placeGroupSupplyInstructions === null || placeGroupSupplyInstructions === void 0 ? void 0 : placeGroupSupplyInstructions.supplyInstructionsByDeliveryType.mailIn;
        if (!mailInInstructions)
            return false;
        const { sampleStability } = mailInInstructions;
        return sampleStability < (3 * DAY_IN_HOURS);
    });
};
const getDaysOfWeek = () => Object.values(DayOfWeek).map((_, i) => i);
/**
 * @param date - The date to check
 * @param timeSlotRestrictions - The time slot restrictions (e.g. 10 AM - 2 PM but in military time)
 * @returns Whether the date is within the time slot hours restrictions
 */
const getIsWithinFridayRestrictions = (date, timeSlotRestrictions) => {
    if (date.timestamp === undefined)
        return false;
    const { timestamp: start } = getSpecificTimeOnDate(date, timeSlotRestrictions.start.toString());
    const { timestamp: end } = getSpecificTimeOnDate(date, timeSlotRestrictions.end.toString());
    if (!start || !end)
        return false;
    return date.timestamp >= start && date.timestamp <= end;
};
/**
 * @param startTime - The beginning timestamp of the range
 * @param endTime - The ending timestamp of the range
 * @param availableDays - The days of the week that are available
 * @param leadTimeInDays - The number of days before the date that the walk-in is available
 * @returns Whether the lead time is after the available days between the two dates
 */
const getIsAfterLeadTime = (startDate, endDate, availableDays, leadTimeInDays) => {
    const normalizedStartDate = dayjs(startDate).startOf('day');
    const normalizedEndDate = dayjs(endDate).startOf('day');
    const daysUntilDate = normalizedEndDate.diff(normalizedStartDate, 'day');
    const availableDaysUntilDate = Array.from({ length: daysUntilDate }, (_, i) => normalizedStartDate.add(i + 1, 'day')).filter((filterDate) => availableDays.includes(filterDate.day())).length;
    return leadTimeInDays <= availableDaysUntilDate;
};
/**
 * @param initialDate - The initial date to check
 * @param daysOpen - The days of the week that are open for walk-in. If null, ignore this validation.
 * @param leadTimeInDays - The number of days before the date that the walk-in is available
 * @param hasShortStability - Whether the samples have a short stability. e.g. less than 72 hours
 * @param timeZone - The time zone of the lab (optional)
 * @param filterWeekend - Whether to filter weekends (optional)
 * @param fridayRestrictions - The time slot restrictions for Fridays in military time (optional)
 * @returns A function that determines whether the date is valid for a walk-in appointment
 *
 * Validations:
 * - The date is after the initial date
 * - The date is a weekday
 * - The date is a Friday if the samples have a short stability
 * - The date is after the lead time
 * - The date is within the time slot restrictions for Fridays if provided
 * - The date is not a weekend if provided
 */
const getLabDateValidator = (initialDate, daysOpen, leadTimeInDays, hasShortStability, timeZone, filterWeekend, fridayRestrictions) => (date) => {
    const dateDay = timeZone ? dayjs(date).tz(timeZone).day() : date.getDay();
    const daysOfWeek = getDaysOfWeek();
    const weekDays = daysOfWeek.slice(1, 6);
    const isOpen = daysOpen ? daysOpen.includes(dateDay) : true;
    const isAfterInitialDate = dayjs(date).isAfter(initialDate);
    const isFridayWithShortStability = hasShortStability && dateDay === FRIDAY;
    const isAfterLeadTime = getIsAfterLeadTime(initialDate, date, weekDays, leadTimeInDays);
    const isWithinRestrictionsWhenFriday = !fridayRestrictions
        || dateDay !== FRIDAY
        || getIsWithinFridayRestrictions(initTimeZoneDate(timeZone, date.getTime()), fridayRestrictions);
    const isWeekend = filterWeekend && (dateDay === SATURDAY || dateDay === SUNDAY);
    return (isOpen
        && isAfterInitialDate
        && isAfterLeadTime
        && !isFridayWithShortStability
        && isWithinRestrictionsWhenFriday
        && !isWeekend);
};
/**
 * Get a function that validates a date for a facility time slot.
 *
 * The function will return true if:
 * - The date is after the latest supply arrival timestamp
 * - The date is valid for a lab date
 * - The date is after the lead time
 * - The date is NOT a Friday if the samples have a short stability
 *
 * @param selectedDrawPlaceID - The selected draw place ID
 * @param labTimeZone - The time zone of the lab
 * @returns A function that validates a date for a facility time slot
 */
const getFacilityTimeValidator = (selectedDrawPlaceID, labTimeZone) => {
    var _a;
    const store = getStore();
    const { labOptions, supplyInstructionConfigOptions, procedures, } = store.state.schedulingFlow;
    const hasLessThan72HoursSampleStability = getHasLessThan72HoursSampleStability((_a = labOptions[selectedDrawPlaceID]) === null || _a === void 0 ? void 0 : _a.placeGroupID, supplyInstructionConfigOptions);
    const latestSupplyArrivalTimestamp = getLatestSupplyArrivalTimestamp(supplyInstructionConfigOptions);
    const fridayRestrictions = store.getters['variables/getFacilityTimesFridayTimeSlotRestrictions'];
    const getIsValidLabDate = getLabDateValidator(new Date(), null, // We aren't worrying about lab opening hours
    0, // lead time is irrelevant
    hasLessThan72HoursSampleStability, labTimeZone, // Lab could be in a different time zone than current
    true, // Filter weekends
    fridayRestrictions);
    // If the date is after the latest supply arrival timestamp, and the date is valid, return true
    return (date) => {
        const isValidLabDate = getIsValidLabDate(new Date(date));
        const isDateAfterLatestSupplyArrivalTimestamp = date > latestSupplyArrivalTimestamp;
        const canPerformProcedureCollection = getCanPerformProcedureCollections(procedures, date, labTimeZone);
        return isValidLabDate && isDateAfterLatestSupplyArrivalTimestamp && canPerformProcedureCollection;
    };
};
const transformLabAppointmentTimes = (placeID, appointmentTimes, timeZone) => {
    // Process appointment times and group by day
    const groupedAppointmentTimes = {};
    // Get lab date validator according to our scheduling config and placeGroup, and supplyInstructionConfigOptions
    const getIsValidLabDate = getFacilityTimeValidator(placeID, timeZone);
    appointmentTimes.forEach((timestamp) => {
        var _a;
        if (!getIsValidLabDate(timestamp))
            return;
        // Get the day timestamp at 12 AM
        const dayTimestamp = dayjs(timestamp)
            .tz(timeZone)
            .startOf('day')
            .utc()
            .valueOf();
        // Doing this for typescript - this would never be null as long
        // as timestamp is not nullish
        if (dayTimestamp === null)
            return;
        // Format the time as string
        const timeString = formatLocalDate(timestamp, 'h:mm a', timeZone);
        if (!groupedAppointmentTimes[dayTimestamp])
            groupedAppointmentTimes[dayTimestamp] = [];
        (_a = groupedAppointmentTimes[dayTimestamp]) === null || _a === void 0 ? void 0 : _a.push({
            id: timestamp.toString(),
            label: timeString,
        });
    });
    return groupedAppointmentTimes;
};
/**
 * Gets the time zone for the lab from it's location. If the lab location's
 * time zone is not set, we source it from the lab location's lat/lng.
 */
const getLabTimeZone = async (selectedDrawPlaceID) => {
    const store = getStore();
    const labOption = store.state.schedulingFlow.labOptions[selectedDrawPlaceID];
    if (!labOption || isLocationOrigin(labOption.location))
        return;
    const { timeZone } = labOption.location;
    if (timeZone)
        return timeZone;
    const { lat, lng } = labOption.location;
    const { timeZoneId } = await getTimeZoneFromLocation([lat, lng]);
    return timeZoneId;
};

export { DAY_IN_HOURS, getHasLessThan72HoursSampleStability, getLabDateValidator, getLabTimeZone, transformLabAppointmentTimes };
